import { Button, ButtonProps } from '@/shadcn-ui/button';
import { cn } from '@/shadcn-ui/utils';
import { colors } from '@/theme/colors';
import Alert from '@/icons/alert';
import ChevronRight from '@/icons/chevron-right';
import InfoThick from '@/icons/info-thick';
import { forwardRef } from 'react';

export interface ButtonBlobProps extends ButtonProps {
  hasBoxShadow?: boolean;
  buttonVariant?: 'info' | 'infoLarge';
  children: React.ReactNode;
}

/**
 * Button that looks like a box with a right chevron or an info icon.
 * @param handleClick click delegate
 * @param hasBoxShadow
 * @param padding
 * @param children children to be rendered inside box
 * @param variant 'info' for light blue variant with info icon instead of chevron
 */
export const ButtonBlob = forwardRef<HTMLButtonElement, ButtonBlobProps>(
  (
    {
      onClick,
      hasBoxShadow = false,
      children,
      buttonVariant,
      className,
      disabled = false,
      ...props
    }: ButtonBlobProps,
    ref
  ) => {
    const Icon = () => {
      switch (buttonVariant) {
        case 'info':
          return <InfoThick color={colors.blue30} />;
        case 'infoLarge':
          return <Alert color={colors.blue30} />;
        default:
          return <ChevronRight />;
      }
    };

    return (
      <Button
        variant="clear"
        onClick={onClick}
        disabled={disabled}
        className={cn('h-auto w-full', className)}
        ref={ref}
        {...props}
      >
        <div
          className={cn('w-full cursor-pointer bg-inherit p-[16px] text-start', {
            'shadow-shadow20': hasBoxShadow,
            'rounded-[16px] border-0 bg-flexcar-neutral90': buttonVariant === 'info',
          })}
        >
          <div className="flex w-full flex-row justify-between">
            <div
              className={cn('w-full', {
                'text-[14px] font-[600]': buttonVariant === 'info',
              })}
            >
              {children}
            </div>

            <div className="flex flex-row items-center">
              <Icon />
            </div>
          </div>
        </div>
      </Button>
    );
  }
);

ButtonBlob.displayName = 'ButtonBlob';
